import { useCallback } from 'react';

import { addToCart, removeFromCart } from 'services/avo';
import { useTrackEvent } from 'services/datalayer/hooks';
import { TicketFragment } from 'types/api/tickets.types';

import { useTicketInfoLookup } from 'components/@tickets/hooks';

import { useEventBundle } from './useEventBundle';
import { mapTicketToDataLayer } from './utils';

// Whenever a quantity of a ticket is changed in the cart
// This can also be that the complete line item is removed
export const useTrackQuantityChange = () => {
  const eventBundle = useEventBundle();
  const lookup = useTicketInfoLookup();

  const trackAdd = useTrackEvent({
    trackFn: addToCart,
    variables: eventBundle,
  });

  const trackRemove = useTrackEvent({
    trackFn: removeFromCart,
    variables: eventBundle,
  });

  const handleTrackAdd = useCallback(
    (ticket: Pick<TicketFragment, 'id' | 'name'>) => {
      const itemData = mapTicketToDataLayer(
        ticket,
        eventBundle,
        lookup(ticket.id),
      );
      trackAdd({
        items: [itemData],
        currency: 'SAR',
        value: itemData.price,
      });
    },
    [eventBundle, lookup, trackAdd],
  );

  const handleTrackRemove = useCallback(
    (ticket: Pick<TicketFragment, 'id' | 'name'>, quantity = 1) => {
      const itemData = [];
      for (let i = 0; i < quantity; i++) {
        itemData.push(
          mapTicketToDataLayer(ticket, eventBundle, lookup(ticket.id)),
        );
      }

      trackRemove({
        items: itemData,
        currency: 'SAR',
        value: itemData.reduce((acc, item) => acc + item.price, 0),
      });
    },
    [eventBundle, lookup, trackRemove],
  );

  return {
    trackAdd: handleTrackAdd,
    trackRemove: handleTrackRemove,
  };
};
